import { memo } from "react"

import { useTranslation } from "react-i18next"
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"

import type { TokenChartData } from "../types"
import type { NameType, Payload, ValueType } from "recharts/types/component/DefaultTooltipContent"
import type { TooltipContentProps } from "recharts/types/component/Tooltip"

import { Activity } from "@/components/icons"
import { EmptyState } from "@/components/ui/empty-state"

import { LoadingSkeleton } from "./admin-dashboard-loading"

const CHART_HEIGHT = {
  LARGE: 320,
} as const

// Custom formatter for token values
const formatTokenValue = (value: number): string => {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(2)}M`
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K`
  }
  return value.toLocaleString()
}

// Custom Tooltip component
function CustomTooltip({ active, payload, label }: TooltipContentProps<ValueType, NameType>) {
  if (!active || payload.length === 0) {
    return null
  }

  return (
    <div className="bg-popover rounded-lg border p-3 shadow-md">
      <p className="text-popover-foreground mb-2 font-medium">{label}</p>
      <div className="space-y-1">
        {payload.map((entry: Payload<ValueType, NameType>, index: number) => {
          const value = typeof entry.value === "number" ? entry.value : 0
          const formattedValue = formatTokenValue(value)
          const fullValue = value.toLocaleString()

          return (
            <div key={index} className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <div className="size-2 rounded-full" style={{ backgroundColor: entry.color }} />
                <span className="text-muted-foreground text-sm capitalize">
                  {entry.dataKey ? String(entry.dataKey) : "value"}:
                </span>
              </div>
              <div className="text-right">
                <span className="text-popover-foreground text-sm font-semibold">
                  {formattedValue}
                </span>
                <span className="text-muted-foreground ml-1 text-xs">({fullValue})</span>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export const TokenUsageChart = memo(function TokenUsageChart({
  data,
  isLoading,
}: {
  data: TokenChartData[] | null | undefined
  isLoading?: boolean
}) {
  const { t } = useTranslation()

  if (isLoading) {
    return <LoadingSkeleton height={CHART_HEIGHT.LARGE} variant="chart" />
  }

  if (!data || data.length === 0 || data.every((d) => d.total === 0)) {
    return (
      <div className="flex h-80 items-center justify-center">
        <EmptyState
          description={t("adminDashboard.charts.tokenUsage.emptyDescription")}
          icon={Activity}
          title={t("adminDashboard.charts.tokenUsage.emptyTitle")}
        />
      </div>
    )
  }

  // Calculate interval for x-axis labels to avoid overcrowding
  const xAxisInterval = data.length > 30 ? Math.floor(data.length / 8) : 0

  return (
    <ResponsiveContainer
      aria-label="Token usage chart showing input, output, and total tokens over time"
      height={CHART_HEIGHT.LARGE}
      width="100%"
    >
      <AreaChart data={data}>
        <defs>
          <linearGradient id="inputGradient" x1="0" x2="0" y1="0" y2="1">
            <stop offset="5%" stopColor="hsl(var(--chart-1))" stopOpacity={0.35} />
            <stop offset="95%" stopColor="hsl(var(--chart-1))" stopOpacity={0} />
          </linearGradient>
          <linearGradient id="outputGradient" x1="0" x2="0" y1="0" y2="1">
            <stop offset="5%" stopColor="hsl(var(--chart-2))" stopOpacity={0.35} />
            <stop offset="95%" stopColor="hsl(var(--chart-2))" stopOpacity={0} />
          </linearGradient>
          <linearGradient id="totalGradient" x1="0" x2="0" y1="0" y2="1">
            <stop offset="5%" stopColor="hsl(var(--chart-4))" stopOpacity={0.35} />
            <stop offset="95%" stopColor="hsl(var(--chart-4))" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid className="stroke-muted" strokeDasharray="3 3" />
        <XAxis
          angle={-45}
          className="text-muted-foreground"
          dataKey="label"
          height={70}
          interval={xAxisInterval}
          minTickGap={20}
          textAnchor="end"
          tick={{ fontSize: 11 }}
        />
        <YAxis
          allowDecimals={false}
          className="text-muted-foreground"
          domain={[0, "auto"]}
          tick={{ fontSize: 12 }}
          tickFormatter={(value) => {
            if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`
            if (value >= 1000) return `${(value / 1000).toFixed(0)}K`
            return value.toString()
          }}
        />
        <Tooltip content={(props) => <CustomTooltip {...props} />} />
        <Legend />
        <Area
          dataKey="input"
          fill="url(#inputGradient)"
          fillOpacity={1}
          stroke="hsl(var(--chart-1))"
          type="monotone"
        />
        <Area
          dataKey="output"
          fill="url(#outputGradient)"
          fillOpacity={1}
          stroke="hsl(var(--chart-2))"
          type="monotone"
        />
        <Area
          dataKey="total"
          fill="url(#totalGradient)"
          fillOpacity={1}
          stroke="hsl(var(--chart-4))"
          type="monotone"
        />
      </AreaChart>
    </ResponsiveContainer>
  )
})
